/**
 * CortexLogo — inline SVG extracted from cortex_logo.svg
 *
 * Two exports:
 *  <CortexMark />        — icon-only (circle + triangles + eye), square
 *  <CortexLogoFull />    — full wordmark (mark + "cortex" + "BY VIREON")
 */

interface MarkProps {
  /** Size of the square bounding box in px (default 36) */
  size?: number;
  className?: string;
}

/** The geometric mark only — circle, triangles, connector lines, eye. */
export function CortexMark({ size = 36, className }: MarkProps) {
  return (
    <svg
      viewBox="15 15 140 140"   /* tight crop around the mark */
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Cortex mark"
      className={className}
    >
      {/* Outer circle */}
      <circle cx="88" cy="90" r="62" fill="none" stroke="currentColor" strokeWidth="2.2" />

      {/* Outer triangle */}
      <polygon
        points="88,28 141.7,118 34.3,118"
        fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"
      />

      {/* Inner triangle */}
      <polygon
        points="88,54 116.4,108 59.6,108"
        fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"
      />

      {/* Connector lines */}
      <line x1="88"    y1="54"  x2="88"    y2="28"  stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <line x1="116.4" y1="108" x2="141.7" y2="118" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <line x1="59.6"  y1="108" x2="34.3"  y2="118" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />

      {/* Node dots */}
      <circle cx="88"    cy="28"  r="6.5" fill="currentColor" />
      <circle cx="141.7" cy="118" r="6.5" fill="currentColor" />
      <circle cx="34.3"  cy="118" r="6.5" fill="currentColor" />

      {/* Center eye — layered circles */}
      <circle cx="88" cy="88" r="17" fill="#d6e8f8" />
      <circle cx="88" cy="88" r="12" fill="#6aaee0" />
      <circle cx="88" cy="88" r="7"  fill="currentColor" />
      <circle cx="88" cy="88" r="2.8" fill="white" />
    </svg>
  );
}

interface FullProps {
  /** Height of the full logo lockup in px (default 32) */
  height?: number;
  className?: string;
}

/** Full horizontal lockup — mark + "cortex" wordmark + "BY VIREON" tagline. */
export function CortexLogoFull({ height = 32, className }: FullProps) {
  // The original SVG viewBox is 500×180; scale uniformly to requested height.
  const width = (500 / 180) * height;
  return (
    <svg
      viewBox="0 0 500 180"
      width={width}
      height={height}
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Cortex by Vireon"
      className={className}
    >
      {/* Outer circle */}
      <circle cx="88" cy="90" r="62" fill="none" stroke="currentColor" strokeWidth="2.2" />

      {/* Outer triangle */}
      <polygon
        points="88,28 141.7,118 34.3,118"
        fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"
      />

      {/* Inner triangle */}
      <polygon
        points="88,54 116.4,108 59.6,108"
        fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"
      />

      {/* Connector lines */}
      <line x1="88"    y1="54"  x2="88"    y2="28"  stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <line x1="116.4" y1="108" x2="141.7" y2="118" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <line x1="59.6"  y1="108" x2="34.3"  y2="118" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />

      {/* Node dots */}
      <circle cx="88"    cy="28"  r="6.5" fill="currentColor" />
      <circle cx="141.7" cy="118" r="6.5" fill="currentColor" />
      <circle cx="34.3"  cy="118" r="6.5" fill="currentColor" />

      {/* Center eye */}
      <circle cx="88" cy="88" r="17" fill="#d6e8f8" />
      <circle cx="88" cy="88" r="12" fill="#6aaee0" />
      <circle cx="88" cy="88" r="7"  fill="currentColor" />
      <circle cx="88" cy="88" r="2.8" fill="white" />

      {/* Wordmark */}
      <text
        x="168" y="103"
        fontFamily="'Gill Sans','Trebuchet MS','Calibri',sans-serif"
        fontSize="64" fontWeight="700" fill="currentColor" letterSpacing="-1"
      >
        cortex
      </text>
      <text
        x="170" y="126"
        fontFamily="'Gill Sans','Trebuchet MS','Calibri',sans-serif"
        fontSize="15" fontWeight="700" fill="currentColor" letterSpacing="5"
      >
        BY VIREON
      </text>
    </svg>
  );
}
